import { ref, computed, type Ref, type ComputedRef } from 'vue'
import { useI18n } from 'vue-i18n'
import { STORAGE_CHUNK_INTERVAL_MS } from '@/constants/recording'
import {
  getIncompleteSessions,
  deleteSession,
  type RecordingSession,
} from '@/services/RecordingDatabase'
import { logUnexpectedFailure } from '@/utils/errorHandling'

/**
 * Extended session info with computed duration and formatted date.
 */
export interface RecoverableSession {
  session: RecordingSession
  formattedDate: string
  durationMinutes: number
  isResumable: boolean
  appointmentName?: string
}

export interface UseSessionRecoveryReturn {
  // State
  recoverableSessions: Ref<RecoverableSession[]>
  isLoading: Ref<boolean>
  error: Ref<string | null>
  isInitialized: Ref<boolean>

  // Computed
  hasRecoverableSessions: ComputedRef<boolean>
  sessionCount: ComputedRef<number>

  // Actions
  checkForIncompleteSessions: () => Promise<void>
  discardSession: (sessionId: string) => Promise<void>
  canResumeSession: (session: RecordingSession) => boolean
  cleanupOldSessions: () => Promise<number>
}

// Maximum age for resumable sessions (24 hours in milliseconds)
const MAX_RESUME_AGE_MS = 24 * 60 * 60 * 1000

const MS_PER_MINUTE = 60 * 1000

// Maximum age before automatic cleanup (7 days in milliseconds)
const MAX_SESSION_AGE_MS = 7 * 24 * 60 * 60 * 1000

/**
 * Composable for detecting and recovering interrupted recording sessions.
 *
 * This composable is used to:
 * 1. Check for incomplete sessions on app mount
 * 2. Provide recovery options (upload, resume, discard)
 * 3. Automatically clean up sessions older than 7 days
 *
 * Usage:
 * ```typescript
 * const { hasRecoverableSessions, recoverableSessions, discardSession } = useSessionRecovery()
 *
 * // Check on mount
 * await checkForIncompleteSessions()
 *
 * // Show recovery dialog if needed
 * if (hasRecoverableSessions.value) {
 *   // Display RecoveryDialog.vue
 * }
 * ```
 */
export function useSessionRecovery(): UseSessionRecoveryReturn {
  const { t, d } = useI18n()

  // State
  const recoverableSessions = ref<RecoverableSession[]>([])
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const isInitialized = ref(false)

  // Computed
  const hasRecoverableSessions = computed(() => recoverableSessions.value.length > 0)
  const sessionCount = computed(() => recoverableSessions.value.length)

  /**
   * Check if a session can be resumed (less than 24 hours old).
   */
  function canResumeSession(session: RecordingSession): boolean {
    const ageMs = Date.now() - session.startedAt
    return ageMs < MAX_RESUME_AGE_MS
  }

  /**
   * How much audio a session holds, in whole minutes, from the number of chunks
   * it wrote. See {@link STORAGE_CHUNK_INTERVAL_MS} for why the count is the
   * only input and how close the answer is.
   *
   * Floored at one minute: a recording short enough to round to zero still has
   * audio in it, and "0 minuten" reads as an empty session to discard.
   */
  function estimateDurationMinutes(session: RecordingSession): number {
    const durationMs = session.totalChunks * STORAGE_CHUNK_INTERVAL_MS
    return Math.max(1, Math.round(durationMs / MS_PER_MINUTE))
  }

  /**
   * Format a session into a RecoverableSession with computed properties.
   */
  function formatSession(session: RecordingSession): RecoverableSession {
    return {
      session,
      formattedDate: d(new Date(session.startedAt), 'long'),
      durationMinutes: estimateDurationMinutes(session),
      isResumable: canResumeSession(session),
      appointmentName: undefined, // Will be populated by the component if appointment exists
    }
  }

  /**
   * Check IndexedDB for incomplete recording sessions.
   * Automatically cleans up sessions older than 7 days.
   */
  async function checkForIncompleteSessions(): Promise<void> {
    isLoading.value = true
    error.value = null

    try {
      // First, clean up old sessions
      await cleanupOldSessions()

      // Get remaining incomplete sessions
      const sessions = await getIncompleteSessions()

      // Format sessions with additional metadata
      recoverableSessions.value = sessions
        .map(formatSession)
        .sort((a, b) => b.session.startedAt - a.session.startedAt) // Most recent first

      isInitialized.value = true
    } catch (err) {
      logUnexpectedFailure('Failed to check for incomplete sessions:', err)
      error.value = t('recording.recovery.checkFailed')
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Discard a session and all its chunks from IndexedDB.
   */
  async function discardSession(sessionId: string): Promise<void> {
    error.value = null

    try {
      await deleteSession(sessionId)

      // Remove from local state
      recoverableSessions.value = recoverableSessions.value.filter(
        (r) => r.session.id !== sessionId
      )
    } catch (err) {
      logUnexpectedFailure('Failed to discard session:', err)
      error.value = t('recording.recovery.discardFailed')
      throw err
    }
  }

  /**
   * Clean up sessions older than 7 days.
   * Returns the number of sessions deleted.
   */
  async function cleanupOldSessions(): Promise<number> {
    try {
      const sessions = await getIncompleteSessions()
      const now = Date.now()
      let deletedCount = 0

      for (const session of sessions) {
        const ageMs = now - session.startedAt
        if (ageMs > MAX_SESSION_AGE_MS) {
          await deleteSession(session.id)
          deletedCount++
        }
      }

      if (deletedCount > 0) {
        console.info(`Cleaned up ${deletedCount} old recording session(s)`)
      }

      return deletedCount
    } catch (err) {
      logUnexpectedFailure('Failed to clean up old sessions:', err)
      return 0
    }
  }

  return {
    // State
    recoverableSessions,
    isLoading,
    error,
    isInitialized,

    // Computed
    hasRecoverableSessions,
    sessionCount,

    // Actions
    checkForIncompleteSessions,
    discardSession,
    canResumeSession,
    cleanupOldSessions,
  }
}
